import {useData} from "@hps/hops-react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {slugify} from "Raildays/RaildaysUtils.js";
import {useCallback, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import Button from "Components/Button.js";
import Card from "Components/Card.js";
import Loader from "Components/Loader.js";
import TextField from "Components/TextField.js";
import RaildaysService from "Services/RaildaysService.js";

const ERROR_VISIT_DATE_EXPIRED = "Sorry, you can no longer post feedback for this visit date.";
const ERROR_FEEDBACK_EXISTS = "We have already recorded your feedback from your visit.";
const ERROR_GENERIC = "Sorry, we could not process your request.";
const ERROR_EMPTY = "Please submit at least one rating or comment.";
const INFO_NO_QUESTIONS = "No questions are available for this feedback id.";
const FEEDBACK_SCORES = [1, 2, 3, 4, 5];

const normaliseFeedbackQuestions = value => {
	if (!Array.isArray(value)) return [];
	return value.map(question => ({
		id: question?.Id,
		body: question?.Body || ""
	})).filter(question => question.id);
};

const resolveSubmittedRailwayName = value => {
	if (value === undefined || value === null) return "";
	if ((typeof value === "string") || (typeof value === "number")) return String(value);
	if (typeof value !== "object") return "";
	return String(value?.Name || "");
};

const RaildaysFeedbackQuestionsView = () => {
	const navigate = useNavigate();
	const {feedbackId} = useParams();

	const [scores, setScores] = useState({});
	const [comments, setComments] = useState({});
	const [submitError, setSubmitError] = useState("");
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [hideForm, setHideForm] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const {data: questionsResponse, loading: questionsLoading} = useData(
		useCallback(() => {
			if (!feedbackId) return Promise.resolve({status: 404, data: []});
			return RaildaysService.getFeedbackQuestions(feedbackId);
		}, [feedbackId])
	);

	const questionStatus = questionsResponse?.status;
	const questions = useMemo(() => {
		if (questionStatus !== 200) return [];
		return normaliseFeedbackQuestions(questionsResponse?.data);
	}, [questionsResponse, questionStatus]);

	const loadState = useMemo(() => {
		if (questionsLoading) return {error: "", success: false, hideForm: false};
		if (questionStatus === 200) return {error: "", success: false, hideForm: false};
		if (questionStatus === 204) return {error: INFO_NO_QUESTIONS, success: true, hideForm: true};
		if (questionStatus === 409) return {error: ERROR_FEEDBACK_EXISTS, success: true, hideForm: true};
		if (questionStatus === 412) return {error: ERROR_VISIT_DATE_EXPIRED, success: false, hideForm: true};
		return {error: ERROR_GENERIC, success: false, hideForm: true};
	}, [questionStatus, questionsLoading]);

	const setScore = useCallback((questionId, score) => {
		setScores(prev => ({...prev, [questionId]: score}));
	}, []);

	const setComment = useCallback((questionId, value) => {
		setComments(prev => ({...prev, [questionId]: value}));
	}, []);

	const handleSubmit = useCallback(async event => {
		event.preventDefault();
		setSubmitError("");
		setSubmitSuccess(false);

		const answers = questions.map(question => {
			const score = scores[question.id];
			const comment = (comments[question.id] || "").trim();
			return {
				questionId: question.id,
				score: score ? Number(score) : null,
				comment: comment || null
			};
		});

		const answersEmpty = answers.every(answer => !answer.score && !answer.comment);
		if (answersEmpty) {
			setSubmitError(ERROR_EMPTY);
			return;
		}

		setSubmitting(true);
		try {
			const {status, data} = await RaildaysService.submitFeedbackAnswers(feedbackId, answers);
			if (status === 409) {
				setSubmitError(ERROR_FEEDBACK_EXISTS);
				setSubmitSuccess(true);
				setHideForm(true);
				return;
			}
			if (status === 412) {
				setSubmitError(ERROR_VISIT_DATE_EXPIRED);
				setHideForm(true);
				return;
			}
			if (status !== 200) {
				setSubmitError(ERROR_GENERIC);
				return;
			}

			const targetSlug = slugify(resolveSubmittedRailwayName(data));
			if (targetSlug) {
				const railwaysData = await RaildaysService.getRailways();
				const list = Array.isArray(railwaysData?.objects) ? railwaysData.objects : (Array.isArray(railwaysData) ? railwaysData : []);
				const exists = list.some(railway => slugify(railway?.Name || "") === targetSlug);
				if (exists) {
					navigate(`/railways/${targetSlug}?feedback=1`);
					return;
				}
			}

			navigate("/?feedback=1");
		}
		catch (requestError) {
			setSubmitError(ERROR_GENERIC);
		}
		finally {
			setSubmitting(false);
		}
	}, [comments, feedbackId, navigate, questions, scores]);

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				mx: {xs: 2, md: 3, lg: 5},
				mb: {xs: 2, md: 3, lg: 5},
				backgroundColor: "#fff",
				borderRadius: 2
			}}>
			<Container maxWidth={false} sx={{py: {xs: 4, md: 6}, px: {xs: 2, md: 4, lg: 6}}}>
				<Breadcrumbs sx={{textTransform: "uppercase", color: "#e2b534"}}>
					<Button label="Home" uri="/" style={{color: "#e2b534"}} variant="text" />
					<Button label="Feedback" uri="/feedback" style={{color: "#e2b534"}} variant="text" />
					<Typography color="#e2b534">Feedback Questions</Typography>
				</Breadcrumbs>

				<Typography variant="h4" sx={{mt: 2, fontWeight: 800, textTransform: "uppercase"}}>
					Feedback Questions
				</Typography>

				<Box sx={{maxWidth: 1024, mx: "auto", mt: 4}}>
					{(loadState.error || submitError) && (
						<Alert severity={(submitSuccess || loadState.success) ? "success" : "error"} sx={{mb: 3}}>
							<Typography>{submitError || loadState.error}</Typography>
						</Alert>
					)}

					{questionsLoading && (
						<Stack alignItems="center" sx={{py: 4}}>
							<Loader />
						</Stack>
					)}

					{(!questionsLoading && !hideForm && !loadState.hideForm) && (
						<Card px={0} py={0} style={{borderRadius: 12, boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)", overflow: "hidden"}}>
							<Box
								sx={{
									p: {xs: 3, md: 4},
									background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)"
								}}>
								<Typography sx={{fontWeight: 800, color: "#111827"}} variant="h6">
									Tell us about your visit
								</Typography>
								<Typography sx={{mt: 1, color: "#475569"}}>
									Please score each area and add any comments you want to share.
								</Typography>
								<Stack direction="row" spacing={1.25} sx={{mt: 2}}>
									{FEEDBACK_SCORES.map(score => (
										<Box
											key={`score-preview-${score}`}
											component="img"
											alt={`Feedback score ${score}`}
											src={`/assets/Raildays/feedback/smiley_${score}.svg`}
											sx={{width: 28, height: 28, opacity: 0.9}}
										/>
									))}
								</Stack>
							</Box>

							<Divider />

							<Box sx={{p: {xs: 3, md: 4}}}>
								<form onSubmit={handleSubmit}>
									<Stack spacing={2}>
										{questions.map(question => (
											<Box
												key={question.id}
												sx={{
													border: "1px solid #e5e7eb",
													borderRadius: 2,
													p: 2.25,
													backgroundColor: "#fff"
												}}>
												<Box
													sx={{
														display: "grid",
														gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
														gap: 2
													}}>
													<Box>
														<Typography variant="body2" sx={{fontWeight: 700, mb: 1}}>
															{question.body}
														</Typography>
														<Box sx={{display: "flex", gap: 1, justifyContent: "center"}}>
															{FEEDBACK_SCORES.map(score => {
																const isSelected = String(scores[question.id] || "") === String(score);
																return (
																	<Box key={`${question.id}-${score}`} sx={{position: "relative"}}>
																		<input
																			checked={isSelected}
																			id={`${question.id}-${score}`}
																			name={`score-${question.id}`}
																			onChange={() => setScore(question.id, score)}
																			style={{
																				border: 0,
																				clip: "rect(0, 0, 0, 0)",
																				height: 1,
																				overflow: "hidden",
																				padding: 0,
																				position: "absolute",
																				whiteSpace: "nowrap",
																				width: 1
																			}}
																			type="radio"
																			value={score}
																		/>
																		<label
																			htmlFor={`${question.id}-${score}`}
																			style={{
																				alignItems: "center",
																				cursor: "pointer",
																				display: "flex",
																				height: 52,
																				justifyContent: "center",
																				width: 52
																			}}>
																			<img
																				alt={`${score}`}
																				src={`/assets/Raildays/feedback/smiley_${score}.svg`}
																				style={{
																					opacity: isSelected ? 1 : 0.35,
																					transition: "opacity 120ms linear",
																					width: "100%"
																				}}
																			/>
																		</label>
																	</Box>
																);
															})}
														</Box>
													</Box>

													<Box>
														<TextField
															controlled={true}
															fullWidth={true}
															label="Any other comments"
															onChange={value => setComment(question.id, value)}
															size="small"
															value={comments[question.id] || ""}
														/>
													</Box>
												</Box>
											</Box>
										))}
									</Stack>

									<Box sx={{display: "flex", justifyContent: {xs: "stretch", md: "flex-end"}, mt: 3}}>
										{submitting ?
											(
												<Loader size={20} />
											) :
											(
												<Button label="Submit Feedback" type="submit" variant="outlined" />
											)}
									</Box>
								</form>
							</Box>
						</Card>
					)}
				</Box>
			</Container>
		</Box>
	);
};

export default RaildaysFeedbackQuestionsView;
